import { useContext, useEffect, useMemo, useRef } from 'react'
import { useAtomValue } from 'jotai'
import { AuthContext } from '../../provider/auth'
import { BasketContext } from '../../provider/basket'
import { Frame } from '../../components/Frame'
import { TestCategories } from '../../components/TestCategories'
import { Outlet, useLocation } from 'react-router-dom'
import {
  ConfirmDialog,
  ConfirmDialogType,
} from '../../components/ConfirmDialog'
import {
  availableTestTagsSubscriptionAtom,
  profilesSubscriptionAtom,
} from '../../store/subscription.atoms'
import AvailableLaboratories from '../../components/AvailableLaboratories'
import SearchButton from '../../components/SearchButton'

const Layout = () => {
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const location = useLocation()
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [{ data: availableTestTagsData }] = useAtomValue(
    availableTestTagsSubscriptionAtom
  )

  const isShippingCatalog = location.pathname.startsWith('/shipping-tests')

  const isTestTagsAvailable = useMemo(
    () => !!availableTestTagsData?.available_test_tag.length,
    [availableTestTagsData?.available_test_tag.length]
  )

  const isTagsVisible = useMemo(() => {
    return (
      isTestTagsAvailable &&
      !isShippingCatalog &&
      (location.pathname === '/tests' ||
        location.pathname.startsWith('/tests/tag') ||
        location.pathname.startsWith('/test'))
    )
  }, [location, isTestTagsAvailable, isShippingCatalog])

  const isShippingTagSidebarVisible = useMemo(
    () =>
      isShippingCatalog &&
      (location.pathname === '/shipping-tests' ||
        location.pathname.startsWith('/shipping-tests/tag')),
    [location.pathname, isShippingCatalog]
  )

  const showTestCategoriesSidebar = useMemo(
    () => isTagsVisible || isShippingTagSidebarVisible,
    [isTagsVisible, isShippingTagSidebarVisible]
  )

  const showPartnersColumn = useMemo(
    () =>
      !!basket.address?.location &&
      (isTagsVisible ||
        location.pathname.startsWith('/shipping-tests')),
    [basket.address?.location, isTagsVisible, location.pathname]
  )

  const isSearchVisible = useMemo(() => {
    const onAnySearchPage =
      location.pathname.startsWith('/tests/search') ||
      location.pathname.startsWith('/shipping-tests/search') ||
      location.pathname.startsWith('/consultation/search') ||
      location.pathname.startsWith('/training/search')
    return (
      !onAnySearchPage &&
      (location.pathname === '/tests' ||
        location.pathname.startsWith('/tests/tag') ||
        location.pathname === '/shipping-tests' ||
        location.pathname.startsWith('/shipping-tests/tag') ||
        location.pathname === '/training' ||
        location.pathname === '/consultation')
    )
  }, [location])

  const isTagsVisibleScreenSm = useMemo(() => {
    return (
      (isTestTagsAvailable &&
        !isShippingCatalog &&
        location.pathname === '/tests') ||
      location.pathname.startsWith('/tests/tag') ||
      (isShippingCatalog &&
        (location.pathname === '/shipping-tests' ||
          location.pathname.startsWith('/shipping-tests/tag')))
    )
  }, [location, isTestTagsAvailable, isShippingCatalog])

  useEffect(() => {
    if (!basket.profile && profilesData?.client_profile.length) {
      basket.setProfile(profilesData?.client_profile[0])
    }
  }, [basket, profilesData])

  useEffect(() => {
    if (auth.accessToken) {
      return
    }

    if (basket.overrideAddress) {
      return
    }
  }, [auth.accessToken, basket.overrideAddress])

  return (
    <Frame
      leftClassName="hidden md:block lg:w-[100px] xl:w-[200px] 2xl:w-[300px]"
      rightClassName="hidden xl:block xl:w-[200px] 2xl:w-[300px]"
      renderLeft={showTestCategoriesSidebar && <TestCategories />}
      renderRight={showPartnersColumn && <AvailableLaboratories />}
    >
      {isSearchVisible && <SearchButton />}
      {isTagsVisibleScreenSm && (
        <div className="md:hidden mt-5">
          <TestCategories />
        </div>
      )}
      <Outlet />
      <ConfirmDialog ref={confirmDialogRef} />
    </Frame>
  )
}

export default Layout
